import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';

import type { HiddenReason } from '@amumin/api-client';
import { t } from '@amumin/i18n';

import { groupHidden, PROFILE_SETTINGS_PATH } from '../lib/profileText.js';
import { appText } from '../state/session.js';

// "3 opties verborgen · Alles tonen" onder een gefilterde lijst (0024; docs/DESIGN.md §8.P.5).
// Na Alles tonen: de verborgen opties gegroepeerd per reden, één kop per groep met Wijzig (naar
// Instellingen · Wat past bij ons gezin). De teller komt van de server: zonder profiel 0 = geen
// regel (een bestaand gezin ziet niets nieuws). Wie de lijst tekent, dimt alleen het beeld.

export interface HiddenOptionsProps<T extends { hidden_reason: HiddenReason }> {
  hidden: ReadonlyArray<T>;
  badge: number;
  /** Tekent de verborgen items van één groep (zelfde tegels als de lijst, gedimd beeld). */
  render: (items: T[]) => ComponentChildren;
  name: string;
}

export function HiddenOptions<T extends { hidden_reason: HiddenReason }>({
  hidden,
  badge,
  render,
  name,
}: HiddenOptionsProps<T>) {
  const open = useSignal(false);
  const a = appText.value;
  const p = a.profile;
  if (badge <= 0 || hidden.length === 0) return null;
  const groups = groupHidden(p, hidden, a.culture.hidden.groupFaithNone);
  return (
    <div data-name={`${name}-hidden`}>
      <p class="hidden-options">
        <span>{t(p.hidden.count, { count: badge })}</span>
        <span aria-hidden="true"> · </span>
        <button
          type="button"
          class="hidden-options-toggle"
          aria-expanded={open.value ? 'true' : 'false'}
          onClick={() => (open.value = !open.value)}
          data-name={`${name}-show-all`}
        >
          {open.value ? p.hidden.showLess : p.hidden.showAll}
        </button>
      </p>
      {open.value
        ? groups.map((g) => (
            <section key={g.key} class="hidden-group" data-name={`${name}-hidden-group`}>
              <h3 class="hidden-group-head">
                {g.heading}
                <span aria-hidden="true"> · </span>
                <a href={PROFILE_SETTINGS_PATH}>{p.hidden.change}</a>
              </h3>
              {render(g.items)}
            </section>
          ))
        : null}
    </div>
  );
}
